import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import Dialog from 'components/dialog';
import FilterSidebar from 'components/filter-sidebar';

import Breadcrumbs from 'components/breadcrumbs';
import Paper from 'components/paper';
import Switch from 'components/switch';
import { useAccountState } from 'contexts/account/account-context';
import PlaygroundTabsHeader from '../../../components/playground-tabs-header';
import SelectSite from 'views/playground/features/my-supply-plan/components/select-site';
import InputSettings from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/input-settings';
import InventoryConsumptionBox from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/inventory-consumption-box';
import NextDeliveryBox from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/next-delivery-box';
import OrdersSection from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/orders-section';
import SettingsSubTabs from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/settings-sub-tabs';
import OFDTimeline from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/timeline/timeline';
import { InputSettingsProvider } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { OrdersProvider, useOrdersState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/orders-context';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';
import { SectionTab } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/section-tab.enum';
import { OrderFeedProvider } from 'views/order-feed/context/order-feed-context';

type Props = {};

const today = dayjs();

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan';

const SiteOverview: React.FC<Props> = () => {
  const intl = useIntl();
  const fromDate = today;
  const history = useHistory();
  const toDate = today.add(90, 'days');
  const params = useParams<OFD2Params>();
  const {
    account: { company },
  } = useAccountState();

  const [showTimeline, setShowTimeline] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');

  const handleSelectedSiteId = () => {
    setSelectedSiteId('');
    history.push('/playground/supply-plan/sites');
  };

  return (
    <>
      <PlaygroundTabsHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: String(company?.addressAccess.find((s) => s.siteErpId === params.siteErpId)?.name),
                url: `${params.siteErpId}/${params.globalSiteId}`,
              },
            ]}
          />
        }
      />
      <Box display={'flex'} color={'white'} flexDirection={'row'} justifyContent={'space-between'} mt={10} mb={10}>
        <Box>
          <Typography fontWeight={'bold'}>{intl.formatMessage({ id: `${PAGE_NAME_ID}.ofdActive` })}</Typography>
        </Box>
        <Box>
          <FilterSidebar
            showClearFilter={!!selectedSiteId || !fromDate.isSame(today) || !toDate.isSame(today.add(90, 'days'))}
            clearFilters={() => {
              handleSelectedSiteId();
            }}>
            <SelectSite />
          </FilterSidebar>
        </Box>
      </Box>
      <Box mb={20} display={'flex'} gap={5} color={'white'}>
        <InventoryConsumptionBox />
        <NextDeliveryBox />
      </Box>
      <Box display={'flex'} width={'100%'} justifyContent={'space-between'}>
        <SettingsSubTabs />
        <Switch
          sx={{ justifyContent: 'center' }}
          label={'Show timeline'}
          value={showTimeline}
          onChange={() => setShowTimeline(!showTimeline)}
        />
      </Box>
      <Paper transparent sx={{ display: 'flex', borderTopLeftRadius: { xs: 0, md: '1rem' }, flexDirection: 'column', gap: 4 }}>
        {showTimeline && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box width={'100%'} borderRadius={2} sx={{ background: 'white' }} mb={10}>
              <OFDTimeline />
            </Box>
          </Box>
        )}
        {params.sectionTab === SectionTab.ORDERS && <OrdersSection />}
        {params.sectionTab === SectionTab.INPUT_SETTINGS && <InputSettings />}
      </Paper>
    </>
  );
};

const SiteOverviewContainer: React.FC<{}> = ({}) => {
  return (
    <OrderFeedProvider>
      <InputSettingsProvider>
        <OrdersProvider>
          <SiteOverview />
        </OrdersProvider>
      </InputSettingsProvider>
    </OrderFeedProvider>
  );
};

export default SiteOverviewContainer;
